import {
  Box,
  IconButton,
  Slide,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { HeatsFilterProvider } from "components/strategyDashboard/heats/context/HeatsFilterContext";
import { HeatsSelectionProvider } from "components/strategyDashboard/heats/context/HeatsSelectionContext";
import { OutlierHeatsDataProvider } from "components/strategyDashboard/heats/context/OutlierHeatsDataProvider";
import { useHeatsData } from "components/strategyDashboard/heats/context/HeatsDataContext";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterialDisplayName } from "hooks/useMaterials";
import { ChemistryElementProvider } from "../heats/chemistryTab/ChemistryElementContext";
import HeatDetailsDrawer from "../heats/HeatDetailsDrawer";
import { useOutlierHeats } from "./OutlierHeatsContext";
import { OutlierHeatsTable } from "./OutlierHeatsTable";

const ROWS_PER_PAGE = 25;

interface OutlierHeatsPanelContentProps {
  heatIds: string[];
  materialName: string;
  page: number;
  setPage: (page: number) => void;
  onClose: () => void;
}

const OutlierHeatsPanelContent = ({
  heatIds,
  materialName,
  page,
  setPage,
  onClose,
}: OutlierHeatsPanelContentProps) => {
  const { availableElements } = useHeatsData();
  const { t } = useTenantTranslation();
  const materialDisplayName = useMaterialDisplayName();

  return (
    <ChemistryElementProvider availableElements={availableElements}>
      <Stack
        gap={2}
        sx={{
          height: "100%",
          p: 2,
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          spacing={2}
          alignItems="center"
        >
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
          <Typography variant="h5">
            {t("outlierHeats")} - {materialDisplayName(materialName)}
          </Typography>
        </Stack>
        <Stack
          sx={{
            width: "73%",
            mx: "auto",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <OutlierHeatsTable materialName={materialName} />
          <TablePagination
            component="div"
            count={heatIds.length}
            page={page}
            onPageChange={(_, newPage) => {
              setPage(newPage);
            }}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[]}
          />
        </Stack>
        <HeatDetailsDrawer highlightedMaterial={materialName} />
      </Stack>
    </ChemistryElementProvider>
  );
};

export const OutlierHeatsPanel = () => {
  const { outlierHeats, closeOutlierHeats, page, setPage } = useOutlierHeats();

  const isOpen = outlierHeats !== null;

  return (
    <Slide in={isOpen} direction="left" mountOnEnter unmountOnExit>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          borderLeft: 1,
          mx: 2,
          mb: 2,
          borderColor: "divider",
          position: "absolute",
          top: 0,
          right: 0,
          backgroundColor: "background.paper",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {outlierHeats ? (
          <OutlierHeatsDataProvider
            heatIds={outlierHeats.heatIds}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
          >
            <HeatsSelectionProvider>
              <HeatsFilterProvider>
                <OutlierHeatsPanelContent
                  heatIds={outlierHeats.heatIds}
                  materialName={outlierHeats.materialName ?? ""}
                  page={page}
                  setPage={setPage}
                  onClose={closeOutlierHeats}
                />
              </HeatsFilterProvider>
            </HeatsSelectionProvider>
          </OutlierHeatsDataProvider>
        ) : null}
      </Box>
    </Slide>
  );
};
